import { PitchShell } from "@/components/pitch-practice/PitchShell";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Mic, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { GrantPersona } from "@/components/pitch-practice/GrantPersona";
import { createSession, deleteSession, listSessions } from "@/lib/pitch-practice/pitch.functions";
import { STAGES, scoreTone } from "@/lib/pitch-practice/grant";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/accelerator/pitch-practice/")({
  head: () => ({
    meta: [
      { title: "My Pitches — Grind Ventures Pitch Room" },
      { name: "description", content: "Start a new practice pitch with Grant Rogers and review past scorecards." },
      { property: "og:title", content: "My Pitches — Grind Ventures Pitch Room" },
      { property: "og:description", content: "Your practice pitch history and scorecards." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: () => (
    <PitchShell>
      <Dashboard />
    </PitchShell>
  ),
});

function Dashboard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const list = useServerFn(listSessions);
  const create = useServerFn(createSession);
  const del = useServerFn(deleteSession);
  const sessions = useQuery({ queryKey: ["sessions"], queryFn: () => list() });

  const [form, setForm] = useState({ startup_name: "", founder_name: "", one_liner: "", stage: "Seed" });

  const start = useMutation({
    mutationFn: () => create({ data: form }),
    onSuccess: ({ id }) => navigate({ to: "/accelerator/pitch-practice/$id", params: { id } }),
    onError: (e: Error) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="min-h-[70vh] bg-background">
      <main className="mx-auto grid max-w-6xl gap-8 px-4 py-10 lg:grid-cols-[1fr_1.1fr]">
        <div className="space-y-6">
          <GrantPersona />
          <form
            className="space-y-4 rounded-lg border border-border bg-card p-5"
            onSubmit={(e) => {
              e.preventDefault();
              start.mutate();
            }}
          >
            <div>
              <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-primary">New session</p>
              <h2 className="mt-1 text-2xl font-bold">Brief Grant before you walk in</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="s">Startup name</Label>
                <Input
                  id="s"
                  required
                  value={form.startup_name}
                  onChange={(e) => setForm({ ...form, startup_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="f">Your name</Label>
                <Input id="f" value={form.founder_name} onChange={(e) => setForm({ ...form, founder_name: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="o">One-liner</Label>
              <Textarea
                id="o"
                rows={2}
                placeholder="e.g. Edge AI that detects drone swarms for bases and stadiums"
                value={form.one_liner}
                onChange={(e) => setForm({ ...form, one_liner: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Stage</Label>
              <div className="flex flex-wrap gap-2">
                {STAGES.map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => setForm({ ...form, stage: s })}
                    className={cn(
                      "rounded border px-3 py-1.5 text-sm transition",
                      form.stage === s ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary",
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <Button type="submit" size="lg" className="w-full" disabled={start.isPending}>
              <Mic /> {start.isPending ? "Opening the room…" : "Enter the pitch room"}
            </Button>
          </form>
        </div>

        <section>
          <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">History</p>
          <h2 className="mt-1 text-2xl font-bold">Past sessions</h2>
          <div className="mt-4 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {sessions.isLoading && <p className="p-5 text-sm text-muted-foreground">Loading…</p>}
            {sessions.error && <p className="p-5 text-sm text-destructive">{(sessions.error as Error).message}</p>}
            {sessions.data?.length === 0 && (
              <p className="p-5 text-sm text-muted-foreground">No pitches yet. Your first scorecard will show up here.</p>
            )}
            {sessions.data?.map((s) => (
              <div key={s.id} className="flex items-center gap-4 p-4">
                <div className={cn("w-14 text-center font-display text-2xl font-extrabold", scoreTone(s.overall_score))}>
                  {s.overall_score ?? "—"}
                </div>
                <Link
                  to={s.status === "scored" ? "/accelerator/pitch-practice/scorecard/$id" : "/accelerator/pitch-practice/$id"}
                  params={{ id: s.id }}
                  className="min-w-0 flex-1 hover:text-primary"
                >
                  <p className="truncate font-semibold">{s.startup_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(s.created_at), "MMM d, yyyy · h:mm a")} · {s.stage}
                    {" · "}
                    {s.status === "scored" ? s.verdict ?? "Scored" : "In progress"}
                  </p>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Delete session"
                  onClick={() => confirm("Delete this session?") && remove.mutate(s.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
